import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Trophy, MessageSquare, X, ArrowDown, ArrowUp, Check, FileDown, Loader2 } from 'lucide-react';
import { api } from '../contexts/AuthContext';
import { toast } from 'sonner';

const RISK_RANK = { Low: 0, Medium: 1, High: 2 };

const HIGHLIGHT = {
  best: 'bg-emerald-50 text-emerald-700 border-emerald-200 ring-1 ring-emerald-300/60',
  worst: 'bg-rose-50 text-rose-700 border-rose-200',
  neutral: 'bg-white text-slate-700 border-slate-200',
};

// Determine best/worst index per metric.
// kind: 'min' (lower=better), 'max' (higher=better), 'risk' (Low=best)
const computeRanks = (suppliers, getValue, kind) => {
  if (!suppliers.length) return [];
  const values = suppliers.map(getValue);
  let bestVal, worstVal;
  if (kind === 'risk') {
    const ranks = values.map((v) => RISK_RANK[v] ?? 1);
    bestVal = Math.min(...ranks);
    worstVal = Math.max(...ranks);
    return ranks.map((r) => (r === bestVal ? 'best' : r === worstVal && bestVal !== worstVal ? 'worst' : 'neutral'));
  }
  if (kind === 'min') {
    bestVal = Math.min(...values);
    worstVal = Math.max(...values);
  } else {
    bestVal = Math.max(...values);
    worstVal = Math.min(...values);
  }
  return values.map((v) => (v === bestVal ? 'best' : v === worstVal && bestVal !== worstVal ? 'worst' : 'neutral'));
};

const fmtPrice = (s) => {
  if (s.price_range_low && s.price_range_high) {
    return `$${s.price_range_low} – $${s.price_range_high}`;
  }
  return `$${s.price}`;
};

const buildMetrics = (suppliers, t) => [
  { key: 'price', label: t('comparisonRowPrice'), kind: 'min',
    getValue: (s) => s.price, render: (s) => <span>{fmtPrice(s)}<span className="text-xs text-slate-400 ml-1">{t('perUnit')}</span></span> },
  { key: 'moq', label: t('comparisonRowMoq'), kind: 'min',
    getValue: (s) => s.moq, render: (s) => <span>{s.moq} {t('units')}</span> },
  { key: 'delivery', label: t('comparisonRowDelivery'), kind: 'min',
    getValue: (s) => s.lead_time_days, render: (s) => <span>{s.lead_time_days} {t('days')}</span> },
  { key: 'reliability', label: t('comparisonRowReliability'), kind: 'max',
    getValue: (s) => s.score_breakdown?.reliability ?? s.reliability_score ?? 0, render: (s) => <span>{s.score_breakdown?.reliability ?? s.reliability_score ?? 0}/100</span> },
  { key: 'total', label: t('comparisonRowTotal'), kind: 'max',
    getValue: (s) => s.score_breakdown?.total ?? s.reliability_score ?? 0, render: (s) => <span className="font-bold">{s.score_breakdown?.total ?? s.reliability_score ?? 0}/100</span> },
  { key: 'risk', label: t('comparisonRowRisk'), kind: 'risk',
    getValue: (s) => s.risk_level, render: (s) => <span>{t(s.risk_level === 'Low' ? 'lowRisk' : s.risk_level === 'Medium' ? 'mediumRisk' : 'highRisk')}</span> },
];

// Identify the winner (highest total) and list its objective strengths.
const computeWinner = (suppliers, t) => {
  if (suppliers.length === 0) return null;
  const totals = suppliers.map((s) => s.score_breakdown?.total ?? s.reliability_score ?? 0);
  const bestIdx = totals.indexOf(Math.max(...totals));
  const winner = suppliers[bestIdx];
  if (suppliers.length < 2) return { winner, reasons: [] };

  const reasons = [];
  const minPrice = Math.min(...suppliers.map((s) => s.price));
  const minMoq = Math.min(...suppliers.map((s) => s.moq));
  const minLead = Math.min(...suppliers.map((s) => s.lead_time_days));
  const maxRel = Math.max(...suppliers.map((s) => s.score_breakdown?.reliability ?? s.reliability_score ?? 0));
  const minRiskRank = Math.min(...suppliers.map((s) => RISK_RANK[s.risk_level] ?? 1));

  if (winner.price === minPrice) reasons.push(t('winsBestPrice'));
  if (winner.lead_time_days === minLead) reasons.push(t('winsFastestDelivery'));
  const winnerRel = winner.score_breakdown?.reliability ?? winner.reliability_score ?? 0;
  if (winnerRel === maxRel) reasons.push(t('winsHighestReliability'));
  if (winner.moq === minMoq) reasons.push(t('winsLowestMoq'));
  if ((RISK_RANK[winner.risk_level] ?? 1) === minRiskRank) reasons.push(t('winsLowestRisk'));
  // Always include this — winner is by definition highest total
  reasons.push(t('winsHighestScore'));

  // De-dupe and limit to 3 most distinctive
  return { winner, reasons: [...new Set(reasons)].slice(0, 3) };
};

// Compact label per metric for mobile strengths/weaknesses lines
const SHORT_LABELS = {
  price: 'comparisonRowPrice',
  moq: 'comparisonRowMoq',
  delivery: 'comparisonRowDelivery',
  reliability: 'comparisonRowReliability',
  total: 'comparisonRowTotal',
  risk: 'comparisonRowRisk',
};

const SupplierComparison = ({ open, onOpenChange, suppliers, t, onGenerateMessage, requestId, language }) => {
  const [exporting, setExporting] = React.useState(false);
  if (!suppliers || suppliers.length < 2) return null;

  const metrics = buildMetrics(suppliers, t);
  const ranksByMetric = metrics.map((m) => computeRanks(suppliers, m.getValue, m.kind));
  const insight = computeWinner(suppliers, t);

  const handleExportPdf = async () => {
    if (!requestId) {
      toast.error(t('pdfError'));
      return;
    }
    setExporting(true);
    try {
      const { data } = await api.post(
        '/api/reports/comparison/pdf',
        {
          request_id: requestId,
          supplier_names: suppliers.map((s) => s.name),
          language: language || 'en',
        },
        { responseType: 'blob' },
      );
      const blob = new Blob([data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `safesourcer-comparison-${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(t('pdfReady'));
    } catch (e) {
      toast.error(t('pdfError'));
    } finally {
      setExporting(false);
    }
  };

  // Per-supplier strengths/weaknesses for mobile cards (short metric labels where this supplier wins/loses)
  const strengthsAndWeaknesses = suppliers.map((s, si) => {
    const strengths = [];
    const weaknesses = [];
    metrics.forEach((m, mi) => {
      const rank = ranksByMetric[mi][si];
      if (rank === 'best') strengths.push(t(SHORT_LABELS[m.key]));
      else if (rank === 'worst') weaknesses.push(t(SHORT_LABELS[m.key]));
    });
    return { strengths, weaknesses };
  });

  // Score badge color for the prominent supplier-card score
  const scoreColor = (v) => (v >= 75
    ? 'bg-emerald-100 text-emerald-700 border-emerald-200'
    : v >= 55 ? 'bg-amber-100 text-amber-700 border-amber-200'
    : 'bg-rose-100 text-rose-700 border-rose-200');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-4xl p-0 gap-0 h-screen max-h-screen w-screen rounded-none md:h-auto md:max-h-[90vh] md:w-auto md:rounded-lg md:p-6 overflow-hidden flex flex-col [&>button.absolute]:hidden md:[&>button.absolute]:block"
        data-testid="comparison-modal"
      >
        {/* Sticky header — mobile shows X close, desktop relies on built-in DialogClose */}
        <div className="sticky top-0 z-10 flex items-center justify-between gap-3 px-4 py-3 border-b border-slate-200 bg-white md:px-0 md:py-0 md:border-0">
          <DialogHeader className="flex-1 text-left space-y-0">
            <DialogTitle className="flex items-center gap-2 text-base md:text-lg">
              <Trophy className="w-5 h-5 text-amber-500" />
              {t('comparisonTitle')}
            </DialogTitle>
            <DialogDescription className="sr-only">
              {t('comparisonTitle')}
            </DialogDescription>
          </DialogHeader>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="md:hidden p-2 -mr-2 rounded-md text-slate-500 hover:bg-slate-100 active:bg-slate-200"
            aria-label="Close"
            data-testid="comparison-mobile-close"
          >
            <X className="w-5 h-5" />
          </button>
          {requestId && (
            <Button
              type="button"
              onClick={handleExportPdf}
              disabled={exporting}
              variant="outline"
              size="sm"
              className="hidden md:inline-flex border-slate-300 hover:bg-slate-50"
              data-testid="comparison-export-pdf"
            >
              {exporting ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <FileDown className="w-4 h-4 mr-1.5" />}
              {exporting ? t('exportingPdf') : t('exportPdf')}
            </Button>
          )}
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto px-4 py-4 md:p-0 md:pt-4">
        {/* Winner summary — compact on mobile */}
        {insight && insight.reasons.length > 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 md:p-4 mb-4" data-testid="comparison-winner-summary">
            <div className="flex items-center gap-2 mb-1.5">
              <Trophy className="w-4 h-4 text-blue-600 shrink-0" />
              <span className="text-xs md:text-sm font-semibold text-blue-700 uppercase tracking-wide">{t('winnerSummary')}</span>
            </div>
            <p className="text-sm text-slate-800">
              <span className="font-bold">{insight.winner.name}</span> {t('winnerExplain')}
            </p>
            <ul className="mt-1.5 ml-5 space-y-0.5 list-disc list-outside text-xs md:text-sm text-slate-700" data-testid="comparison-winner-reasons">
              {insight.reasons.map((r) => (
                <li key={r}>{r}</li>
              ))}
            </ul>
          </div>
        )}

        {/* DESKTOP: grid table (unchanged) */}
        <div
          className="hidden md:grid gap-px bg-slate-200 rounded-lg overflow-hidden border border-slate-200 text-sm"
          style={{ gridTemplateColumns: `160px repeat(${suppliers.length}, minmax(0, 1fr))` }}
          data-testid="comparison-table-desktop"
        >
          {/* Header row */}
          <div className="bg-slate-100 p-3" />
          {suppliers.map((s) => (
            <div key={s.name} className="bg-slate-100 p-3 font-semibold text-slate-900 truncate" data-testid="comparison-supplier-header">
              {s.name}
            </div>
          ))}
          {/* Metric rows */}
          {metrics.map((m, mi) => (
            <React.Fragment key={m.key}>
              <div className="bg-slate-50 p-3 font-medium text-xs uppercase text-slate-600 tracking-wide">{m.label}</div>
              {suppliers.map((s, si) => {
                const rank = ranksByMetric[mi][si];
                return (
                  <div
                    key={`${m.key}-${s.name}`}
                    className={`p-3 border ${HIGHLIGHT[rank]} flex items-center gap-1.5`}
                    data-testid={`comparison-cell-${m.key}-${si}`}
                  >
                    {rank === 'best' && <Check className="w-3.5 h-3.5 text-emerald-600 shrink-0" />}
                    {rank === 'worst' && <ArrowDown className="w-3.5 h-3.5 text-rose-500 shrink-0" />}
                    {m.render(s)}
                  </div>
                );
              })}
            </React.Fragment>
          ))}
          {/* Action row */}
          <div className="bg-slate-50 p-3" />
          {suppliers.map((s) => (
            <div key={`act-${s.name}`} className="bg-white p-3">
              <Button
                onClick={() => onGenerateMessage(s)}
                size="sm"
                className="w-full bg-blue-600 hover:bg-blue-700"
                data-testid="comparison-generate-message"
              >
                <MessageSquare className="w-3.5 h-3.5 mr-1.5" />
                {t('generateMessage')}
              </Button>
            </div>
          ))}
        </div>

        {/* MOBILE: compact card-based layout */}
        <div className="md:hidden space-y-3" data-testid="comparison-mobile">
          {suppliers.map((s, si) => {
            const total = s.score_breakdown?.total ?? s.reliability_score ?? 0;
            const isWinner = insight && insight.winner.name === s.name;
            const sw = strengthsAndWeaknesses[si];
            // Compact metrics: price, moq, delivery, risk only — total is the prominent score
            const compactRows = [
              { key: 'price', label: t('comparisonRowPrice'), value: <>{fmtPrice(s)}<span className="text-[10px] text-slate-400 ml-0.5">{t('perUnit')}</span></>, mi: 0 },
              { key: 'moq', label: t('comparisonRowMoq'), value: <>{s.moq} {t('units')}</>, mi: 1 },
              { key: 'delivery', label: t('comparisonRowDelivery'), value: <>{s.lead_time_days} {t('days')}</>, mi: 2 },
              { key: 'risk', label: t('comparisonRowRisk'), value: t(s.risk_level === 'Low' ? 'lowRisk' : s.risk_level === 'Medium' ? 'mediumRisk' : 'highRisk'), mi: 5 },
            ];
            return (
              <div
                key={s.name}
                className={`rounded-xl border ${isWinner ? 'border-blue-400 ring-1 ring-blue-200' : 'border-slate-200'} bg-white overflow-hidden`}
                data-testid="comparison-mobile-card"
              >
                {/* Header: name + score badge */}
                <div className="flex items-center justify-between gap-3 px-3 py-2.5 border-b border-slate-100">
                  <div className="min-w-0 flex-1">
                    <h4 className="font-semibold text-slate-900 text-sm truncate" data-testid="comparison-mobile-supplier-name">{s.name}</h4>
                    {isWinner && (
                      <span className="inline-flex items-center gap-1 mt-0.5 text-[10px] font-medium text-blue-700">
                        <Trophy className="w-3 h-3" /> {t('winnerSummary')}
                      </span>
                    )}
                  </div>
                  <span
                    className={`shrink-0 text-xs font-bold px-2.5 py-1 rounded-full border ${scoreColor(total)}`}
                    data-testid="comparison-mobile-score"
                  >
                    {total}/100
                  </span>
                </div>

                {/* Compact 2x2 stats grid */}
                <div className="grid grid-cols-2 gap-px bg-slate-100">
                  {compactRows.map((row) => {
                    const rank = ranksByMetric[row.mi][si];
                    return (
                      <div
                        key={row.key}
                        className="bg-white px-3 py-2"
                        data-testid={`comparison-mobile-row-${row.key}-${si}`}
                      >
                        <div className="flex items-center gap-1 mb-0.5">
                          <span className="text-[10px] text-slate-500 uppercase tracking-wide">{row.label}</span>
                          {rank === 'best' && (
                            <span className="inline-flex items-center gap-0.5 text-[9px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-1.5 leading-tight" data-testid="comparison-mobile-best-badge">
                              <ArrowUp className="w-2.5 h-2.5" />{t('bestBadge')}
                            </span>
                          )}
                          {rank === 'worst' && (
                            <span className="inline-flex items-center gap-0.5 text-[9px] font-bold text-rose-700 bg-rose-50 border border-rose-200 rounded-full px-1.5 leading-tight" data-testid="comparison-mobile-worst-badge">
                              <ArrowDown className="w-2.5 h-2.5" />{t('worstBadge')}
                            </span>
                          )}
                        </div>
                        <div className="text-sm font-semibold text-slate-800 truncate">{row.value}</div>
                      </div>
                    );
                  })}
                </div>

                {/* Strengths / Weaknesses chips */}
                {(sw.strengths.length > 0 || sw.weaknesses.length > 0) && (
                  <div className="px-3 py-2 space-y-1 border-t border-slate-100">
                    {sw.strengths.length > 0 && (
                      <div className="flex items-start gap-1.5 text-xs" data-testid="comparison-mobile-strengths">
                        <Check className="w-3.5 h-3.5 text-emerald-600 mt-0.5 shrink-0" />
                        <div className="flex-1">
                          <span className="text-slate-500">{t('strengthsLabel')}: </span>
                          <span className="text-emerald-700 font-medium">{sw.strengths.join(' · ')}</span>
                        </div>
                      </div>
                    )}
                    {sw.weaknesses.length > 0 && (
                      <div className="flex items-start gap-1.5 text-xs" data-testid="comparison-mobile-weaknesses">
                        <ArrowDown className="w-3.5 h-3.5 text-rose-500 mt-0.5 shrink-0" />
                        <div className="flex-1">
                          <span className="text-slate-500">{t('weaknessesLabel')}: </span>
                          <span className="text-rose-700 font-medium">{sw.weaknesses.join(' · ')}</span>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Action */}
                <div className="px-3 py-2.5 border-t border-slate-100 bg-slate-50">
                  <Button
                    onClick={() => onGenerateMessage(s)}
                    size="sm"
                    className="w-full bg-blue-600 hover:bg-blue-700 h-9"
                    data-testid="comparison-mobile-generate-message"
                  >
                    <MessageSquare className="w-3.5 h-3.5 mr-1.5" />
                    {t('generateMessage')}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Mobile Export PDF (shown after supplier cards) */}
        {requestId && (
          <div className="md:hidden mt-4 pb-2">
            <Button
              type="button"
              onClick={handleExportPdf}
              disabled={exporting}
              variant="outline"
              className="w-full h-11 border-slate-300 hover:bg-slate-50"
              data-testid="comparison-export-pdf-mobile"
            >
              {exporting ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <FileDown className="w-4 h-4 mr-1.5" />}
              {exporting ? t('exportingPdf') : t('exportPdf')}
            </Button>
          </div>
        )}

        {/* Desktop legend (hidden on mobile to reduce clutter) */}
        <div className="hidden md:flex items-center justify-center gap-4 mt-4 text-xs text-slate-500">
          <span className="flex items-center gap-1.5">
            <span className="inline-block w-3 h-3 rounded bg-emerald-100 border border-emerald-300" />
            {t('bestValue')}
          </span>
          <span className="flex items-center gap-1.5">
            <span className="inline-block w-3 h-3 rounded bg-rose-100 border border-rose-300" />
            {t('worstValue')}
          </span>
          <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)} className="ml-auto" data-testid="comparison-close-button">
            <X className="w-3.5 h-3.5 mr-1" /> {t('cancel')}
          </Button>
        </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SupplierComparison;
